import axios from 'axios';
import * as config from 'src/config';
import { FETCH_STATUS } from 'src/constants';
import {
  BACKLOGS_SYNC_STATUS,
  TBacklogs,
  TBacklogsFilters,
  TBacklogsPostgres,
  TBacklogsPostgresEvents,
  TBacklogsSnowflake,
  TBacklogsSnowflakeEvents,
  TBacklogsSyncSnowflakeEventsPayload,
} from 'src/reducers/backlogs';
import { Nullable } from 'src/types';
import { SortableSessionsDataProps } from 'src/views/Backlogs/VSnowflake/SortableSessions/SortableSessions';
import { SosImageProps } from 'src/views/Backlogs/VSnowflake/SortableSessions/Sos/Sos';
import { v4 as uuidv4 } from 'uuid';

export const SET_BACKLOGS_SERVICE_APPLETS = 'backlogs/SET_BACKLOGS_SERVICE_APPLETS';
export const SET_BACKLOGS = 'backlogs/SET_BACKLOGS';
export const SET_BACKLOGS_SNOWFLAKE = 'backlogs/SET_BACKLOGS_SNOWFLAKE';
export const SET_BACKLOGS_POSTGRES = 'backlogs/SET_BACKLOGS_POSTGRES';
export const SET_BACKLOGS_FILTERS = 'backlogs/SET_BACKLOGS_FILTERS';
export const SET_BACKLOGS_SNOWFLAKE_EVENTS = 'backlogs/SET_BACKLOGS_SNOWFLAKE_EVENTS';
export const SET_BACKLOGS_POSTGRES_EVENTS = 'backlogs/SET_BACKLOGS_POSTGRES_EVENTS';
export const SET_BACKLOGS_POSTGRES_VALIDATED_EVENTS = 'backlogs/SET_BACKLOGS_POSTGRES_VALIDATED_EVENTS';
export const SET_BACKLOGS_SNOWFLAKE_PINNED_EVENTS = 'backlogs/SET_BACKLOGS_SNOWFLAKE_PINNED_EVENTS';
export const SET_BACKLOGS_POSTGRES_PINNED_EVENTS = 'backlogs/SET_BACKLOGS_POSTGRES_PINNED_EVENTS';
export const SET_BACKLOGS_FULLSCREEN_IMAGE = 'backlogs/SET_BACKLOGS_FULLSCREEN_IMAGE';
export const CLEAR_BACKLOGS_SERVICE_APPLETS = 'backlogs/CLEAR_BACKLOGS_SERVICE_APPLETS';
export const CLEAR_BACKLOGS = 'backlogs/CLEAR_BACKLOGS';

export const setBacklogsServiceApplets = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_SERVICE_APPLETS, data: value });
  };
};

export const setBacklogsFilters = (value: Partial<TBacklogsFilters>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_FILTERS, data: value });
  };
};

export const setBacklogs = (value: Partial<TBacklogs>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS, data: value });
  };
};

export const setBacklogsSnowflake = (value: Partial<TBacklogsSnowflake>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_SNOWFLAKE, data: value });
  };
};

export const setBacklogsPostgres = (value: Partial<TBacklogsPostgres>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_POSTGRES, data: value });
  };
};

export const setBacklogsSnowflakeEvents = (value: Partial<TBacklogsSnowflakeEvents>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_SNOWFLAKE_EVENTS, data: value });
  };
};

export const setBacklogsPostgresEvents = (value: Partial<TBacklogsPostgresEvents>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_POSTGRES_EVENTS, data: value });
  };
};

export const setBacklogsPostgresValidatedEvents = (value: Partial<TBacklogsPostgresEvents>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_POSTGRES_VALIDATED_EVENTS, data: value });
  };
};

export const syncBacklogsSnowflakeEvents = (payload: TBacklogsSyncSnowflakeEventsPayload) => {
  return async (dispatch) => {
    dispatch({ type: SET_BACKLOGS_SNOWFLAKE_EVENTS, fetchStatus: FETCH_STATUS.PENDING });

    return axios
      .post(`${config.endpoints.base_url}v1/events/batch`, payload)
      .then((res) => {
        dispatch({ type: SET_BACKLOGS_SNOWFLAKE_EVENTS, fetchStatus: FETCH_STATUS.RESOLVED });
        return res;
      })
      .catch((err) => {
        dispatch({ type: SET_BACKLOGS_SNOWFLAKE_EVENTS, fetchStatus: FETCH_STATUS.ERROR });
        throw new Error(err);
      });
  };
};

export const syncBacklogsPostgresEvents = (payload: TBacklogsSyncSnowflakeEventsPayload) => {
  return async (dispatch) => {
    dispatch({ type: SET_BACKLOGS_POSTGRES_EVENTS, fetchStatus: FETCH_STATUS.PENDING });

    return axios
      .put(`${config.endpoints.base_url}v2/events`, payload)
      .then((res) => {
        dispatch({ type: SET_BACKLOGS_POSTGRES_EVENTS, fetchStatus: FETCH_STATUS.RESOLVED });
        return res;
      })
      .catch((err) => {
        dispatch({ type: SET_BACKLOGS_POSTGRES_EVENTS, fetchStatus: FETCH_STATUS.ERROR });
        throw new Error(err);
      });
  };
};

export const submitBacklogsSnowflakeEvents = ({
  data,
  calcStatus,
}: {
  data: SortableSessionsDataProps;
  calcStatus: BACKLOGS_SYNC_STATUS;
}) => {
  const isForComplete = calcStatus === BACKLOGS_SYNC_STATUS.COMPLETE;
  const payload: TBacklogsSyncSnowflakeEventsPayload = {
    sessions: [
      {
        session_id: data.id,
        app_code: 'sos',
        start_timestamp: data.sos[0]?.sos_in?.timestampUTC ?? data.timestampUTC ?? '',
        end_timestamp: data.sos[data.sos.length - 1]?.sos_out?.timestampUTC ?? data.timestampUTC ?? '',
        compliance_list: [],
        site_tz: data.siteTz,
        network_id: 0,
        network_name: 'viana',
        site_name: data.siteName,
        site_id: data.siteID,
        doc_id: data.id,
        critical_events: [],
        calc_status: calcStatus,
        status: calcStatus,
        validations: data.sos?.flatMap((x) => {
          const genNonSessionId = uuidv4();
          const hasReachIn = x.sos_reach_in;
          const hasReachOut = x.sos_reach_out;
          const completeEvent = {
            non_session_id: genNonSessionId,
            event_code: x.eventCode ?? '',
            event_location_id: x.sos_in?.locationID ?? 0,
            event_location_name: x.sos_in?.locationName ?? '',
            event_site_tz: data.siteTz,
            event_site_timestamp_utc: x.sos_in?.timestampUTC ?? '',
            event_network_id: 0,
            event_network_name: 'viana',
            event_asset: '', //need pa mahibaw.an asa kuhaon
            event_timestamp: x.sos_in?.timestampUTC ?? '',
            event_start_timestamp: x.sos_in?.timestampUTC ?? '',
            event_end_timestamp: x.sos_out?.timestampUTC ?? '',
            original_event_code: x.eventCode ?? '',
            original_session_id: x.sos_in?.originalSessionId ?? '',
            session_id: data.id,
            calc_non_session_id: null,
            status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
            date_validated: new Date().toISOString(),
          };
          const reachInEvent = {
            non_session_id: x.sos_reach_in?.id,
            event_code: x.sos_reach_in?.eventCode ?? '',
            event_location_id: x.sos_reach_in?.locationID ?? 0,
            event_location_name: x.sos_reach_in?.locationName ?? '',
            event_site_tz: data.siteTz,
            event_site_timestamp_utc: x.sos_reach_in?.timestampUTC ?? '',
            event_network_id: 0,
            event_network_name: 'viana',
            event_asset: '', //need pa mahibaw.an asa kuhaon
            event_timestamp: x.sos_reach_in?.timestampUTC ?? '',
            event_start_timestamp: x.sos_reach_in?.timestampUTC ?? '',
            event_end_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            original_event_code: x.sos_reach_in?.eventCode ?? '',
            original_session_id: x.sos_reach_in?.originalSessionId ?? '',
            session_id: data.id,
            calc_non_session_id: genNonSessionId,
            status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
            date_validated: new Date().toISOString(),
          };
          const reachOutEvent = {
            non_session_id: x.sos_reach_out?.id,
            event_code: x.sos_reach_out?.eventCode ?? '',
            event_location_id: x.sos_reach_out?.locationID ?? 0,
            event_location_name: x.sos_reach_out?.locationName ?? '',
            event_site_tz: data.siteTz,
            event_site_timestamp_utc: x.sos_reach_out?.timestampUTC ?? '',
            event_network_id: 0,
            event_network_name: 'viana',
            event_asset: '', //need pa mahibaw.an asa kuhaon
            event_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            event_start_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            event_end_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            original_event_code: x.sos_reach_out?.eventCode ?? '',
            original_session_id: x.sos_reach_out?.originalSessionId ?? '',
            session_id: data.id,
            calc_non_session_id: genNonSessionId,
            status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
            date_validated: new Date().toISOString(),
          };
          const nonCompleteEvent = [
            {
              non_session_id: x.sos_in?.id,
              event_code: x.sos_in?.eventCode ?? '',
              event_location_id: x.sos_in?.locationID ?? 0,
              event_location_name: x.sos_in?.locationName ?? '',
              event_site_tz: data.siteTz,
              event_site_timestamp_utc: x.sos_in?.timestampUTC ?? '',
              event_network_id: 0,
              event_network_name: 'viana',
              event_asset: '', //need pa mahibaw.an asa kuhaon
              event_timestamp: x.sos_in?.timestampUTC ?? '',
              event_start_timestamp: x.sos_in?.timestampUTC ?? '',
              event_end_timestamp: x.sos_out?.timestampUTC ?? '',
              original_event_code: x.sos_in?.eventCode ?? '',
              original_session_id: x.sos_in?.originalSessionId ?? '',
              session_id: data.id,
              calc_non_session_id: genNonSessionId,
              status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
              date_validated: new Date().toISOString(),
            },
            {
              non_session_id: x.sos_out?.id,
              event_code: x.sos_out?.eventCode ?? '',
              event_location_id: x.sos_out?.locationID ?? 0,
              event_location_name: x.sos_out?.locationName ?? '',
              event_site_tz: data.siteTz,
              event_site_timestamp_utc: x.sos_out?.timestampUTC ?? '',
              event_network_id: 0,
              event_network_name: 'viana',
              event_asset: '',
              event_timestamp: x.sos_out?.timestampUTC ?? '',
              event_start_timestamp: x.sos_in?.timestampUTC ?? '',
              event_end_timestamp: x.sos_out?.timestampUTC ?? '',
              original_event_code: x.sos_out?.eventCode ?? '',
              original_session_id: x.sos_out?.originalSessionId ?? '',
              session_id: data.id,
              calc_non_session_id: genNonSessionId,
              status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
              date_validated: new Date().toISOString(),
            },
          ];
          if (hasReachIn) {
            nonCompleteEvent.push(reachInEvent);
          }
          if (hasReachOut) {
            nonCompleteEvent.push(reachOutEvent);
          }
          if (isForComplete) {
            nonCompleteEvent.push(completeEvent);
          }
          return nonCompleteEvent;
        }),
      },
    ],
  };
  return async (dispatch) => {
    dispatch({ type: SET_BACKLOGS_SNOWFLAKE_EVENTS, fetchStatus: FETCH_STATUS.PENDING });

    return axios
      .post(`${config.endpoints.base_url}v1/events/batch`, payload)
      .then((res) => {
        dispatch({ type: SET_BACKLOGS_SNOWFLAKE_EVENTS, fetchStatus: FETCH_STATUS.RESOLVED });
        return res;
      })
      .catch((err) => {
        dispatch({ type: SET_BACKLOGS_SNOWFLAKE_EVENTS, fetchStatus: FETCH_STATUS.ERROR });
        throw new Error(err);
      });
  };
};

export const submitBacklogsPostgresEvents = ({
  data,
  calcStatus,
}: {
  data: SortableSessionsDataProps;
  calcStatus: BACKLOGS_SYNC_STATUS;
}) => {
  const isForComplete = calcStatus === BACKLOGS_SYNC_STATUS.COMPLETE;
  const payload: TBacklogsSyncSnowflakeEventsPayload = {
    sessions: [
      {
        session_id: data.id,
        app_code: 'sos',
        start_timestamp: data.sos[0]?.sos_in?.timestampUTC ?? data.timestampUTC ?? '',
        end_timestamp: data.sos[data.sos.length - 1]?.sos_out?.timestampUTC ?? data.timestampUTC ?? '',
        compliance_list: [],
        site_tz: data.siteTz,
        network_id: 0,
        network_name: 'viana',
        site_name: data.siteName,
        site_id: data.siteID,
        doc_id: data.id,
        critical_events: [],
        calc_status: calcStatus,
        status: calcStatus,
        validations: data.sos?.flatMap((x) => {
          const genNonSessionId = uuidv4();
          const hasReachIn = x.sos_reach_in;
          const hasReachOut = x.sos_reach_out;
          const completeEvent = {
            non_session_id: genNonSessionId,
            event_code: x.eventCode ?? '',
            event_location_id: x.sos_in?.locationID ?? 0,
            event_location_name: x.sos_in?.locationName ?? '',
            event_site_tz: data.siteTz,
            event_site_timestamp_utc: x.sos_in?.timestampUTC ?? '',
            event_network_id: 0,
            event_network_name: 'viana',
            event_asset: '', //need pa mahibaw.an asa kuhaon
            event_timestamp: x.sos_in?.timestampUTC ?? '',
            event_start_timestamp: x.sos_in?.timestampUTC ?? '',
            event_end_timestamp: x.sos_out?.timestampUTC ?? '',
            original_event_code: x.eventCode ?? '',
            original_session_id: x.sos_in?.originalSessionId ?? '',
            session_id: data.id,
            calc_non_session_id: null,
            status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
            date_validated: new Date().toISOString(),
          };
          const reachInEvent = {
            non_session_id: x.sos_reach_in?.id,
            event_code: x.sos_reach_in?.eventCode ?? '',
            event_location_id: x.sos_reach_in?.locationID ?? 0,
            event_location_name: x.sos_reach_in?.locationName ?? '',
            event_site_tz: data.siteTz,
            event_site_timestamp_utc: x.sos_reach_in?.timestampUTC ?? '',
            event_network_id: 0,
            event_network_name: 'viana',
            event_asset: '', //need pa mahibaw.an asa kuhaon
            event_timestamp: x.sos_reach_in?.timestampUTC ?? '',
            event_start_timestamp: x.sos_reach_in?.timestampUTC ?? '',
            event_end_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            original_event_code: x.sos_reach_in?.eventCode ?? '',
            original_session_id: x.sos_reach_in?.originalSessionId ?? '',
            session_id: data.id,
            calc_non_session_id: genNonSessionId,
            status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
            date_validated: new Date().toISOString(),
          };
          const reachOutEvent = {
            non_session_id: x.sos_reach_out?.id,
            event_code: x.sos_reach_out?.eventCode ?? '',
            event_location_id: x.sos_reach_out?.locationID ?? 0,
            event_location_name: x.sos_reach_out?.locationName ?? '',
            event_site_tz: data.siteTz,
            event_site_timestamp_utc: x.sos_reach_out?.timestampUTC ?? '',
            event_network_id: 0,
            event_network_name: 'viana',
            event_asset: '', //need pa mahibaw.an asa kuhaon
            event_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            event_start_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            event_end_timestamp: x.sos_reach_out?.timestampUTC ?? '',
            original_event_code: x.sos_reach_out?.eventCode ?? '',
            original_session_id: x.sos_reach_out?.originalSessionId ?? '',
            session_id: data.id,
            calc_non_session_id: genNonSessionId,
            status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
            date_validated: new Date().toISOString(),
          };
          const nonCompleteEvent = [
            {
              non_session_id: x.sos_in?.id,
              event_code: x.sos_in?.eventCode ?? '',
              event_location_id: x.sos_in?.locationID ?? 0,
              event_location_name: x.sos_in?.locationName ?? '',
              event_site_tz: data.siteTz,
              event_site_timestamp_utc: x.sos_in?.timestampUTC ?? '',
              event_network_id: 0,
              event_network_name: 'viana',
              event_asset: '', //need pa mahibaw.an asa kuhaon
              event_timestamp: x.sos_in?.timestampUTC ?? '',
              event_start_timestamp: x.sos_in?.timestampUTC ?? '',
              event_end_timestamp: x.sos_out?.timestampUTC ?? '',
              original_event_code: x.sos_in?.eventCode ?? '',
              original_session_id: x.sos_in?.originalSessionId ?? '',
              session_id: data.id,
              calc_non_session_id: genNonSessionId,
              status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
              date_validated: new Date().toISOString(),
            },
            {
              non_session_id: x.sos_out?.id,
              event_code: x.sos_out?.eventCode ?? '',
              event_location_id: x.sos_out?.locationID ?? 0,
              event_location_name: x.sos_out?.locationName ?? '',
              event_site_tz: data.siteTz,
              event_site_timestamp_utc: x.sos_out?.timestampUTC ?? '',
              event_network_id: 0,
              event_network_name: 'viana',
              event_asset: '',
              event_timestamp: x.sos_out?.timestampUTC ?? '',
              event_start_timestamp: x.sos_in?.timestampUTC ?? '',
              event_end_timestamp: x.sos_out?.timestampUTC ?? '',
              original_event_code: x.sos_out?.eventCode ?? '',
              original_session_id: x.sos_out?.originalSessionId ?? '',
              session_id: data.id,
              calc_non_session_id: genNonSessionId,
              status: isForComplete ? calcStatus : BACKLOGS_SYNC_STATUS.FOR_HMI,
              date_validated: new Date().toISOString(),
            },
          ];
          if (hasReachIn) {
            nonCompleteEvent.push(reachInEvent);
          }
          if (hasReachOut) {
            nonCompleteEvent.push(reachOutEvent);
          }
          if (isForComplete) {
            nonCompleteEvent.push(completeEvent);
          }
          return nonCompleteEvent;
        }),
      },
    ],
  };
  return async (dispatch) => {
    dispatch({ type: SET_BACKLOGS_POSTGRES, data: { isPublishing: true } });

    return axios
      .put(`${config.endpoints.base_url}v2/events`, payload)
      .then((res) => {
        dispatch({ type: SET_BACKLOGS_POSTGRES, data: { isPublishing: false } });
        return res;
      })
      .catch((err) => {
        dispatch({ type: SET_BACKLOGS_POSTGRES, data: { isPublishing: false } });
        throw new Error(err);
      });
  };
};

export const publishBacklogsPostgresEvents = (variables: { dtFrom: string; dtTo: string; sites: Array<number> }) => {
  return async (dispatch) => {
    dispatch({ type: SET_BACKLOGS_POSTGRES, data: { isPublishing: true } });
    return axios
      .post(`${config.endpoints.base_url}v2/events/publish`, variables)
      .then((res) => {
        dispatch({ type: SET_BACKLOGS_POSTGRES, data: { isPublishing: false } });
        return res;
      })
      .catch((err) => {
        dispatch({ type: SET_BACKLOGS_POSTGRES, data: { isPublishing: false } });
        throw new Error(err);
      });
  };
};

export const setBacklogsSnowflakePinnedEvents = (value: Array<SortableSessionsDataProps>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_SNOWFLAKE_PINNED_EVENTS, data: value });
  };
};

export const setBacklogsPostgresPinnedEvents = (value: Array<SortableSessionsDataProps>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_POSTGRES_PINNED_EVENTS, data: value });
  };
};

export const setBacklogsFullscreenImage = (value: Nullable<Partial<SosImageProps>>) => {
  return (dispatch) => {
    dispatch({ type: SET_BACKLOGS_FULLSCREEN_IMAGE, data: value });
  };
};

export const clearBacklogsServiceApplets = () => {
  return (dispatch) => {
    dispatch({ type: CLEAR_BACKLOGS_SERVICE_APPLETS });
  };
};

export const clearBacklogs = () => {
  return (dispatch) => {
    dispatch({ type: CLEAR_BACKLOGS });
  };
};
